import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import { Refresh, FilterList } from '@mui/icons-material';
import { expenseReimbursementService } from '../../services/expenseReimbursementService';
import usePersistedListFilters from '../../hooks/usePersistedListFilters';
import { formatDateDisplay } from '../../utils/dateUtils';
import { formatCurrency } from '../CurrencyTextField';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'paid', label: 'Paid' },
  { value: 'rejected', label: 'Rejected' },
];

const LIST_FILTERS_DEFAULTS = {
  statusFilter: 'pending'
};

const statusColor = (status) => {
  switch (status) {
    case 'approved':
      return 'info';
    case 'paid':
      return 'success';
    case 'rejected':
      return 'error';
    default:
      return 'warning';
  }
};

/** Prevent Strict Mode / remounts from marking the same receipt Paid twice. */
const markPaidInFlight = new Set();
/** Prevent double-approve from email ?approve= links. */
const approveInFlight = new Set();

const ExpenseReimbursementsPanel = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    values: listFilters,
    setField: setListFilter,
    clear: clearListFilters,
    hasActiveFilters
  } = usePersistedListFilters('wm_expense_reimbursements_list_filters', LIST_FILTERS_DEFAULTS);
  const statusFilter = listFilters.statusFilter;
  const setStatusFilter = useCallback((v) => setListFilter('statusFilter', v), [setListFilter]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editRow, setEditRow] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editReceiptDate, setEditReceiptDate] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editStatus, setEditStatus] = useState('pending');
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const goToDetail = (id) => navigate(`/hr/expense-reimbursements/${id}`);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await expenseReimbursementService.list({
        status: statusFilter || undefined,
        limit: 200,
      });
      setRows(res.data ?? []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load reimbursements');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const markPaidRaw = searchParams.get('markPaid');
    if (!markPaidRaw) return;
    const id = parseInt(markPaidRaw, 10);
    if (!Number.isFinite(id) || markPaidInFlight.has(id)) return;
    markPaidInFlight.add(id);

    const next = new URLSearchParams(searchParams);
    next.delete('markPaid');
    setSearchParams(next, { replace: true });

    (async () => {
      try {
        setError('');
        setSuccess('');
        const listRes = await expenseReimbursementService.list({ limit: 500 });
        const row = (listRes.data ?? []).find((r) => Number(r.id) === id);
        if (!row) {
          setError(`Receipt #${id} was not found.`);
          return;
        }
        if (row.status === 'paid') {
          setStatusFilter('paid');
          setSuccess(`Receipt #${id} is already marked as reimbursed (Paid).`);
          return;
        }
        if (row.status !== 'approved') {
          setStatusFilter(row.status || 'pending');
          setError(
            `Receipt #${id} is currently "${row.status}". Approve it first, then use REIMBURSED to mark Paid.`
          );
          return;
        }

        await expenseReimbursementService.update(id, { status: 'paid' });
        setStatusFilter('paid');
        setSuccess(`Receipt #${id} marked as reimbursed (Paid).`);
        window.dispatchEvent(new CustomEvent('expense-reimbursements-updated'));
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to mark receipt as Paid');
      }
    })();
    // Intentionally run once for the inbound markPaid link.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sales supervisor email APPROVE → /hr/expense-reimbursements?approve=:id
  useEffect(() => {
    const approveRaw = searchParams.get('approve');
    if (!approveRaw) return;
    const id = parseInt(approveRaw, 10);
    if (!Number.isFinite(id) || approveInFlight.has(id)) return;
    approveInFlight.add(id);

    const next = new URLSearchParams(searchParams);
    next.delete('approve');
    setSearchParams(next, { replace: true });

    (async () => {
      try {
        setError('');
        setSuccess('');
        const listRes = await expenseReimbursementService.list({ limit: 500 });
        const row = (listRes.data ?? []).find((r) => Number(r.id) === id);
        if (!row) {
          setError(`Receipt #${id} was not found.`);
          return;
        }
        if (row.status === 'approved' || row.status === 'paid') {
          setStatusFilter(row.status);
          setSuccess(
            row.status === 'paid'
              ? `Receipt #${id} is already marked Paid.`
              : `Receipt #${id} is already approved.`
          );
          return;
        }
        if (row.status !== 'pending') {
          setStatusFilter(row.status || '');
          setError(`Receipt #${id} is currently "${row.status}" and cannot be approved from this link.`);
          return;
        }

        await expenseReimbursementService.update(id, { status: 'approved' });
        setStatusFilter('approved');
        setSuccess(
          `Receipt #${id} approved. The Expense Reimbursement notification list has been emailed so they can mark it REIMBURSED.`
        );
        window.dispatchEvent(new CustomEvent('expense-reimbursements-updated'));
        await load();
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to approve receipt');
      }
    })();
    // Intentionally run once for the inbound approve link.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sales supervisor email Review → /hr/expense-reimbursements?review=:id → detail page
  useEffect(() => {
    const reviewRaw = searchParams.get('review');
    if (!reviewRaw) return;
    const id = parseInt(reviewRaw, 10);
    if (!Number.isFinite(id)) return;

    const next = new URLSearchParams(searchParams);
    next.delete('review');
    setSearchParams(next, { replace: true });
    navigate(`/hr/expense-reimbursements/${id}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openEdit = (row) => {
    setEditRow(row);
    setEditAmount(row.amount != null ? String(row.amount) : '');
    setEditReceiptDate(row.receipt_date ? String(row.receipt_date).slice(0, 10) : '');
    setEditNotes(row.notes || '');
    setEditStatus(row.status || 'pending');
  };

  const handleSave = async () => {
    if (!editRow) return;
    try {
      setSaving(true);
      await expenseReimbursementService.update(editRow.id, {
        status: editStatus,
        amount: editAmount === '' ? null : editAmount,
        receipt_date: editReceiptDate === '' ? null : editReceiptDate,
        notes: editNotes,
      });
      setEditRow(null);
      await load();
      window.dispatchEvent(new CustomEvent('expense-reimbursements-updated'));
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteRow) return;
    setDeleting(true);
    setError('');
    try {
      await expenseReimbursementService.delete(deleteRow.id);
      setDeleteRow(null);
      window.dispatchEvent(new CustomEvent('expense-reimbursements-updated'));
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const employeeLabel = (row) => {
    const name = [row.first_name, row.last_name].filter(Boolean).join(' ');
    return row.employee_number ? `${name} (#${row.employee_number})` : name;
  };

  return (
    <Paper sx={{ p: 2, mt: embedded ? 0 : 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        {!embedded && (
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Expense reimbursements
          </Typography>
        )}
        {embedded && <Box sx={{ flexGrow: 1 }} />}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value || 'all'} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {hasActiveFilters ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterList />}
            onClick={clearListFilters}
          >
            Clear filters
          </Button>
        ) : null}
        <IconButton onClick={load} disabled={loading} title="Refresh">
          <Refresh />
        </IconButton>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Review employee receipts, approve for reimbursement, then mark Paid after payroll. Double-click a
        row (or use View) to open full details.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Submitted</TableCell>
              <TableCell>Receipt date</TableCell>
              <TableCell>Employee</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Device</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No receipts in this filter.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  onDoubleClick={() => goToDetail(row.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{formatDateDisplay(row.submitted_at)}</TableCell>
                  <TableCell>{row.receipt_date ? formatDateDisplay(row.receipt_date) : '—'}</TableCell>
                  <TableCell>{employeeLabel(row)}</TableCell>
                  <TableCell>{row.amount != null ? formatCurrency(row.amount) : '—'}</TableCell>
                  <TableCell>
                    <Chip label={row.status} size="small" color={statusColor(row.status)} />
                  </TableCell>
                  <TableCell>{row.device_name || '—'}</TableCell>
                  <TableCell
                    align="right"
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => e.stopPropagation()}
                  >
                    <Button size="small" onClick={() => goToDetail(row.id)}>
                      View
                    </Button>
                    <Button size="small" onClick={() => openEdit(row)}>
                      Edit
                    </Button>
                    <Button size="small" color="error" onClick={() => setDeleteRow(row)}>
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!editRow} onClose={() => !saving && setEditRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reimbursement — {editRow ? employeeLabel(editRow) : ''}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            label="Amount ($)"
            type="number"
            inputProps={{ step: 0.01, min: 0 }}
            value={editAmount}
            onChange={(e) => setEditAmount(e.target.value)}
            sx={{ mt: 1, mb: 1.5 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Receipt date"
            type="date"
            value={editReceiptDate}
            onChange={(e) => setEditReceiptDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 1.5 }}
          />
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            label="Job/Notes"
            multiline
            minRows={2}
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRow(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteRow} onClose={() => !deleting && setDeleteRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete reimbursement?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteRow
              ? `Permanently delete the receipt for ${employeeLabel(deleteRow)}? This cannot be undone.`
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRow(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ExpenseReimbursementsPanel;
